import { getInvestmentRiskAnalysis, getBestInvestmentBank } from '../utils/aiService';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getServices } from '../firebase/firestore';
import { Service, Period, Investment } from '../types';
import { getDateRange, formatCurrency, calculateFutureValue } from '../utils/helpers';
import { getAiAnalysis } from '../utils/aiService';
import { calculateTaxes } from '../utils/taxService';
import FinancialDecisionDashboard from '../components/FinancialDecisionDashboard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LoadingOverlay from '../components/LoadingOverlay';
import '../styles/Reports.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';

const Reports: React.FC = () => {
    const { user, reloadUser } = useAuth();
    useEffect(() => {
      if (reloadUser) reloadUser();
    }, [user, reloadUser]);
    // Verifica se o perfil está completo para IA (valores válidos)
    const isProfileCompleteForAI = user && Number(user.age) > 0 && user.occupation && Number(user.monthlyIncome) > 0 && user.riskProfile && user.investmentGoals && user.investmentGoals.length > 0;
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [period, setPeriod] = useState<Period>('monthly');
  const [financialDecisions, setFinancialDecisions] = useState<any[]>([]);
  const [investmentSuggestions, setInvestmentSuggestions] = useState<Investment[]>([]);
  const [bestBanks, setBestBanks] = useState<string[]>([]);
  const [loadingDecisions, setLoadingDecisions] = useState(false);
  const [loadingPremiumAnalysis, setLoadingPremiumAnalysis] = useState(false);

  // Small human-friendly summary metrics
  const [revenueChangePercent, setRevenueChangePercent] = useState<number | null>(null);
  const [prevRevenue, setPrevRevenue] = useState<number | null>(null);
  const [netAfterTax, setNetAfterTax] = useState<number | null>(null);

  // Custom date range filter (YYYY-MM-DD)
  const [startDateStr, setStartDateStr] = useState<string>('');
  const [endDateStr, setEndDateStr] = useState<string>('');
  const [customRangeApplied, setCustomRangeApplied] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      // Se um intervalo customizado estiver aplicado, usa esse intervalo
      let start: Date;
      let end: Date;
      if (startDateStr && endDateStr && customRangeApplied) {
        start = new Date(startDateStr + 'T00:00:00');
        end = new Date(endDateStr + 'T23:59:59');
      } else {
        const range = getDateRange(period);
        start = range.start;
        end = range.end;
      }

      const data = await getServices(user.uid, start, end);
      setServices(data);

      // Calcular período anterior de mesma duração para comparação (simples)
      const durationMs = end.getTime() - start.getTime();
      const prevEnd = new Date(start.getTime() - 1000);
      const prevStart = new Date(prevEnd.getTime() - durationMs);
      const prevData = await getServices(user.uid, prevStart, prevEnd);

      const currentRevenue = data.reduce((s, it) => s + it.value, 0);
      const previousRevenue = prevData.reduce((s, it) => s + it.value, 0);
      const change = previousRevenue === 0 ? (currentRevenue > 0 ? 100 : 0) : ((currentRevenue - previousRevenue) / previousRevenue) * 100;
      setRevenueChangePercent(Number(change.toFixed(1)));
      // armazenar previousRevenue para exibição simples
      setPrevRevenue(previousRevenue);

      // Calcula net after tax para o período
      const impostoTotal = user ? calculateTaxes(currentRevenue, user) : 0;
      setNetAfterTax(Number((currentRevenue - impostoTotal).toFixed(2)));

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  }, [user, period, startDateStr, endDateStr, customRangeApplied]);

  const handleFinancialDecisions = async () => {
    if (user?.isPremium) return; // Premium não usa esse botão
    if (!isProfileCompleteForAI) {
      alert('Complete seu perfil para receber sugestões de investimento por IA.');
      return;
    }
    setLoadingDecisions(true);
    try {
      const { start, end } = getDateRange(period);
      const serviceData = await getServices(user.uid, start, end);
      const totalRev = serviceData.reduce((sum, s) => sum + s.value, 0);
      const totalProf = serviceData.reduce((sum, s) => sum + (s.value * (s.profitMargin / 100)), 0);
      const impostoTotal = user ? calculateTaxes(totalRev, user) : 0;
      const saldo = totalRev - impostoTotal;
      const { investmentSuggestions, financialDecisionSuggestions } = await getAiAnalysis(
        totalRev,
        totalProf,
        impostoTotal,
        user?.riskProfile || 'moderate',
        saldo
      );

      // Agrupar por tipo e pegar os 3 melhores de cada tipo
      const grouped: { [type: string]: Investment[] } = {};
      investmentSuggestions.forEach(inv => {
        if (!grouped[inv.type]) grouped[inv.type] = [];
        grouped[inv.type].push(inv);
      });
      const topInvestments: Investment[] = [];
      Object.values(grouped).forEach(arr => {
        arr.sort((a, b) => b.expectedReturn - a.expectedReturn);
        topInvestments.push(...arr.slice(0, 3));
      });

      // Buscar análise de risco e melhor banco via IA para cada investimento
      const enrichedInvestments = await Promise.all(topInvestments.map(async (inv) => {
        const riskAnalysis = await getInvestmentRiskAnalysis(inv.type, user?.riskProfile || 'moderate', inv.suggestedAmount);
        const bestBank = await getBestInvestmentBank(inv.type, user?.riskProfile || 'moderate', inv.suggestedAmount);
        return { ...inv, riskAnalysis, bestBank };
      }));

      setInvestmentSuggestions(enrichedInvestments);
      setFinancialDecisions(financialDecisionSuggestions);

      // Exibir o melhor banco do primeiro investimento como destaque
      setBestBanks(enrichedInvestments.length > 0 ? [enrichedInvestments[0].bestBank] : []);
    } catch (error) {
      console.error('Erro ao gerar decisões:', error);
    } finally {
      setLoadingDecisions(false);
    }
  };

  // Função real para análise premium — usa os últimos 12 meses por padrão (ou intervalo customizado se aplicado)
  const handlePremiumAnalysis = async () => {
    if (!user) return;
    setLoadingPremiumAnalysis(true);
    try {
      // Define intervalo: usa intervalo customizado se aplicado, senão últimos 12 meses
      let start: Date;
      let end: Date;
      if (startDateStr && endDateStr && customRangeApplied) {
        start = new Date(startDateStr + 'T00:00:00');
        end = new Date(endDateStr + 'T23:59:59');
      } else {
        end = new Date();
        start = new Date();
        start.setFullYear(end.getFullYear() - 1);
      }

      const serviceData = await getServices(user.uid, start, end);
      const totalRev = serviceData.reduce((sum, s) => sum + s.value, 0);
      const totalProf = serviceData.reduce((sum, s) => sum + (s.value * (s.profitMargin / 100)), 0);
      const impostoTotal = user ? calculateTaxes(totalRev, user) : 0;
      const saldo = totalRev - impostoTotal;

      const { investmentSuggestions, financialDecisionSuggestions } = await getAiAnalysis(
        totalRev,
        totalProf,
        impostoTotal,
        user?.riskProfile || 'moderate',
        saldo
      );

      // Agrupar por tipo e pegar os 3 melhores de cada tipo
      const grouped: { [type: string]: any[] } = {};
      investmentSuggestions.forEach(inv => {
        if (!grouped[inv.type]) grouped[inv.type] = [];
        grouped[inv.type].push(inv);
      });
      const topInvestments: any[] = [];
      Object.values(grouped).forEach((arr: any[]) => {
        arr.sort((a: any, b: any) => b.expectedReturn - a.expectedReturn);
        topInvestments.push(...arr.slice(0, 3));
      });

      // Enriquecer com análise de risco e melhor banco (assíncrono)
      const enrichedInvestments = await Promise.all(topInvestments.map(async (inv: any) => {
        const riskAnalysis = await getInvestmentRiskAnalysis(inv.type, user?.riskProfile || 'moderate', inv.suggestedAmount);
        const bestBank = await getBestInvestmentBank(inv.type, user?.riskProfile || 'moderate', inv.suggestedAmount);
        return { ...inv, riskAnalysis, bestBank };
      }));

      // Atualiza estado para renderizar no dashboard
      setInvestmentSuggestions(enrichedInvestments as any);
      setFinancialDecisions(financialDecisionSuggestions as any);
      setBestBanks(enrichedInvestments.length > 0 ? [enrichedInvestments[0].bestBank] : []);

      // Roda scroll suave para o dashboard
      setTimeout(() => {
        const el = document.querySelector('.financial-decision-dashboard');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    } catch (error: any) {
      console.error('Erro ao gerar análise premium:', error);
      alert('Não foi possível gerar a análise financeira no momento. Tente novamente mais tarde.');
    } finally {
      setLoadingPremiumAnalysis(false);
    }
  };

  // Exporta decisões para CSV (simples)
  const exportDecisionsToCSV = () => {
    if (!financialDecisions || financialDecisions.length === 0) { alert('Nenhuma decisão para exportar.'); return; }
    const headers = ['Título', 'Descrição', 'Impacto', 'Ações'];
    const rows = financialDecisions.map((d: any) => [d.title || '', d.description || '', d.impact || '', Array.isArray(d.actionItems) ? d.actionItems.join('; ') : '']);
    const csv = [headers, ...rows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analise_decisoes_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalRevenue = services.reduce((sum, s) => sum + s.value, 0);
  const totalProfit = services.reduce((sum, s) => sum + (s.value * (s.profitMargin / 100)), 0);

  // Cálculo de impostos
  const estimatedTax = user ? calculateTaxes(totalRevenue, user) : 0;

  const servicesGrouped = services.reduce((acc: any, service) => {
    const date = new Date(service.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    if (!acc[date]) {
      acc[date] = { date, receita: 0, lucro: 0 };
    }
    acc[date].receita += service.value;
    acc[date].lucro += (service.value * (service.profitMargin / 100));
    return acc;
  }, {});

  const chartData = Object.values(servicesGrouped).slice(-10);

  return (
    <div className="reports-page">
      <header className="page-header">
        <button onClick={() => navigate('/')} className="back-btn">← Voltar</button>
        <h1>Relatórios</h1>
      </header>

      {/* Resumo simples e objetivo */}
      <div className="report-summary" style={{ marginBottom: 18, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="summary-card" style={{ flex: '1 1 320px' }}>
          <h3>Resumo Rápido</h3>
          <p style={{ margin: '6px 0', color: '#555' }}>
            Período: <strong>{customRangeApplied && startDateStr && endDateStr ? `${new Date(startDateStr).toLocaleDateString('pt-BR')} → ${new Date(endDateStr).toLocaleDateString('pt-BR')}` : 'Últimos 12 meses'}</strong>
          </p>
          <p style={{ margin: '6px 0' }}><strong>Receita:</strong> {formatCurrency(totalRevenue)}</p>
          <p style={{ margin: '6px 0' }}><strong>Lucro:</strong> {formatCurrency(totalProfit)}</p>
          <p style={{ margin: '6px 0' }}><strong>Depois de impostos:</strong> {netAfterTax !== null ? formatCurrency(netAfterTax) : '—'}</p>
          <p style={{ margin: '6px 0', color: '#666' }}>
            {revenueChangePercent !== null ? (revenueChangePercent > 0 ? `Receita ${revenueChangePercent}% acima do período anterior` : revenueChangePercent < 0 ? `Receita ${Math.abs(revenueChangePercent)}% abaixo do período anterior` : 'Receita está estável em relação ao período anterior') : ''}
          </p>
          {prevRevenue !== null && (
            <p style={{ margin: '6px 0', color: '#666' }}><small>Período anterior: {formatCurrency(prevRevenue)}</small></p>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="primary" onClick={() => { setCustomRangeApplied(false); setStartDateStr(''); setEndDateStr(''); setPeriod('monthly'); }}>Ver último mês</button>
            <button className="secondary" onClick={() => exportDecisionsToCSV()}>Exportar Decisões (CSV)</button>
            <button onClick={() => window.print()}>Imprimir/Exportar PDF</button>
          </div>
        </div>

        {/* Top 3 ações recomendadas pela IA (linguagem simples) */}
        <div className="top-actions" style={{ flex: '1 1 360px' }}>
          <h3 style={{ marginTop: 0 }}>Top 3 Ações Recomendadas</h3>
          {financialDecisions && financialDecisions.length > 0 ? (
            financialDecisions.slice(0,3).map((d, idx) => (
              <div key={idx} className="action-card" style={{ background: '#fff', padding: 12, borderRadius: 10, marginBottom: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{d.title}</strong>
                  <span className={`confidence-badge`}>{d.impact && d.impact.toLowerCase().includes('positivo') ? 'Alta' : 'Média'}</span>
                </div>
                <p style={{ margin: '6px 0', color: '#444' }}>{d.description}</p>
                {d.actionItems && (
                  <ul style={{ margin: '6px 0 8px 18px', color: '#333' }}>
                    {d.actionItems.slice(0,3).map((a: string, i: number) => <li key={i}>{a}</li>)}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="primary" onClick={() => navigate('/services')}>Aplicar</button>
                  <button className="secondary" onClick={() => exportDecisionsToCSV()}>Salvar</button>
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: '#666' }}>
              Nenhuma análise gerada ainda. Clique em <strong>Gerar Análise Financeira</strong> para receber recomendações simples e acionáveis.
            </div>
          )}
        </div>
      </div>

      <div className="period-selector" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {(['daily', 'weekly', 'monthly', 'yearly'] as Period[]).map(p => (
          <button
            key={p}
            className={period === p ? 'active' : ''}
            onClick={() => { setPeriod(p); setCustomRangeApplied(false); setStartDateStr(''); setEndDateStr(''); }}
          >
            {p === 'daily' && 'Dia'}
            {p === 'weekly' && 'Semana'}
            {p === 'monthly' && 'Mês'}
            {p === 'yearly' && 'Ano'}
          </button>
        ))}

        {/* Custom date range */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 12 }}>Intervalo</label>
          <input type="date" value={startDateStr} onChange={e => setStartDateStr(e.target.value)} />
          <span style={{ fontSize: 14 }}>→</span>
          <input type="date" value={endDateStr} onChange={e => setEndDateStr(e.target.value)} />
          <button className="primary" onClick={() => { if (!startDateStr || !endDateStr) { alert('Selecione data inicial e final'); return; } setCustomRangeApplied(true); loadData(); }}>Aplicar</button>
          <button onClick={() => { setCustomRangeApplied(false); setStartDateStr(''); setEndDateStr(''); loadData(); }}>Limpar</button>
        </div>
      </div>

      {/* Gráfico de relatórios no topo */}
      <div className="reports-chart-top">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="receita" fill="#4F8EF7" name="Receita" />
            <Bar dataKey="lucro" fill="#00C49F" name="Lucro" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Financial Decision Dashboard - Premium */}
      {user?.isPremium && (
        <FinancialDecisionDashboard
          decisions={financialDecisions}
          loading={loadingDecisions}
          onGenerateSuggestions={handleFinancialDecisions}
          totalRevenue={totalRevenue}
          totalProfit={totalProfit}
          totalTaxes={estimatedTax}
          monthlyProfit={totalProfit}
        />
      )}

      {investmentSuggestions.length > 0 && (
        <div className="investments-section">
          <h3> Sugestões de Investimento (My Money IA)</h3>
          {bestBanks.length > 0 && (
            <div className="best-bank">
              <strong>Melhor banco de investimento do momento:</strong>
              <span style={{ marginLeft: 8, color: '#4F8EF7', fontWeight: 600 }}>{bestBanks[0]}</span>
            </div>
          )}
          {investmentSuggestions.map((investment, index) => (
            <div key={index} className="investment-card">
              <div className="investment-header">
                <h4>{investment.type}</h4>
                <span className={`risk-badge ${investment.risk}`} style={{ color: 'red', fontWeight: 600 }}>
                  {investment.risk === 'low' && 'Baixo Risco'}
                  {investment.risk === 'medium' && 'Médio Risco'}
                  {investment.risk === 'high' && 'Alto Risco'}
                </span>
              </div>
              <p className="investment-description">{investment.description}</p>
              <div className="investment-details">
                <div className="detail">
                  <span>Retorno Esperado:</span>
                  <strong>{investment.expectedReturn.toFixed(2)}% a.a.</strong>
                </div>
                <div className="detail">
                  <span>Valor Sugerido:</span>
                  <strong>{formatCurrency(investment.suggestedAmount)}</strong>
                </div>
                <div className="detail">
                  <span>Análise de Risco IA:</span>
                  <strong style={{ color: 'red' }}>{investment.riskAnalysis}</strong>
                </div>
                <div className="detail">
                  <span>Melhor banco para esse investimento:</span>
                  <strong style={{ color: '#4F8EF7' }}>{investment.bestBank}</strong>
                </div>
              </div>
              <div className="projection-section">
                <h4> Projeção de Ganhos</h4>
                <p>Reinvestindo {formatCurrency(investment.suggestedAmount)} todo mês:</p>
                <div className="projection-grid">
                  <div className="projection-item">
                    <span>6 meses:</span>
                    <strong>{formatCurrency(calculateFutureValue(investment.suggestedAmount, investment.expectedReturn, 0.5))}</strong>
                  </div>
                  <div className="projection-item">
                    <span>1 ano:</span>
                    <strong>{formatCurrency(calculateFutureValue(investment.suggestedAmount, investment.expectedReturn, 1))}</strong>
                  </div>
                  <div className="projection-item">
                    <span>3 anos:</span>
                    <strong>{formatCurrency(calculateFutureValue(investment.suggestedAmount, investment.expectedReturn, 3))}</strong>
                  </div>
                  <div className="projection-item">
                    <span>5 anos:</span>
                    <strong>{formatCurrency(calculateFutureValue(investment.suggestedAmount, investment.expectedReturn, 5))}</strong>
                  </div>
                  <div className="projection-item">
                    <span>10 anos:</span>
                    <strong>{formatCurrency(calculateFutureValue(investment.suggestedAmount, investment.expectedReturn, 10))}</strong>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <p className="disclaimer">
            <span style={{display:'inline-flex',alignItems:'center',gap:8}}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg> Sugestões geradas por IA (My Money IA). Consulte um assessor financeiro antes de investir.</span>
          </p>
        </div>
      )}

      {/* Botão IA para plano free, só aparece se perfil completo */}
      {!user?.isPremium && (
        <div className="ai-suggestion-section">
          <button
            className={`ai-investment-btn-modern${!isProfileCompleteForAI || loadingDecisions ? ' disabled' : ''}`}
            onClick={handleFinancialDecisions}
            disabled={!isProfileCompleteForAI || loadingDecisions}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
              {loadingDecisions ? 'Buscando sugestões...' : 'Sugestão de Investimento IA'}
            </span>
          </button>
          {!isProfileCompleteForAI && (
            <div className="ai-profile-warning" style={{ color: '#e53e3e', marginTop: 8, fontWeight: 500 }}>
              Preencha seu perfil para ativar sugestões de investimento por IA.
            </div>
          )}
        </div>
      )}
      {user?.isPremium && services.length > 0 && (
        <div className="ai-suggestion-section">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
            <button
              className={`ai-investment-btn-modern${loadingPremiumAnalysis ? ' disabled' : ''}`}
              onClick={handlePremiumAnalysis}
              disabled={loadingPremiumAnalysis}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                {loadingPremiumAnalysis ? 'Processando análise financeira...' : 'Gerar Análise Financeira'}
              </span>
            </button>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <small style={{ color: '#666' }}>
                {customRangeApplied && startDateStr && endDateStr ? `Intervalo: ${new Date(startDateStr).toLocaleDateString('pt-BR')} → ${new Date(endDateStr).toLocaleDateString('pt-BR')}` : 'Intervalo: Últimos 12 meses'}
              </small>
              {financialDecisions && financialDecisions.length > 0 && (
                <button className="secondary" onClick={exportDecisionsToCSV}>Exportar Relatório</button>
              )}
            </div>
          </div>

          {loadingPremiumAnalysis && (
            <LoadingOverlay show={true} message="Processando análise financeira..." />
          )}
        </div>
      )}
      <div className="services-summary">
        <h3>Resumo de Lançamentos ({services.length})</h3>
        <div className="services-table">
          {services.map(service => (
            <div key={service.id} className="table-row">
              <div>
                <strong>{service.description}</strong>
                {service.client && <span className="client-badge"><FontAwesomeIcon icon={faUser} /> {service.client}</span>}
                <small>{new Date(service.date).toLocaleDateString('pt-BR')}</small>
              </div>
              <div className="row-amounts">
                <span>{formatCurrency(service.value)}</span>
                <span className="green">{formatCurrency(service.value * (service.profitMargin / 100))}</span>
                <span className="margin-badge">{service.profitMargin.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reports;
